import React, {
 useState, useEffect, useCallback, useContext
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Save, Loader2
} from 'lucide-react';
import {
 bexSchemaService
} from 'shared/api/services/BEX';
import {
 SchemaColumnDataModel
} from 'shared/api/services/BEXO/Types/Bexo.types';
import {
 Context
} from 'shared/context/GlobalState';
import {
 SchemaListItem, SchemaField
} from '../../types/schema.types';
import SchemaFieldsTab from '../schema-editor/SchemaFieldsTab';

interface SchemaPropertiesTabProps {
    schema: SchemaListItem;
    onSaved: () => void;
}

let fieldIdCounter = 0;
function generateFieldId(): string {
    fieldIdCounter += 1;
    return `prop_field_${Date.now()}_${fieldIdCounter}`;
}

function columnToField(col: SchemaColumnDataModel): SchemaField {
    return {
        ...col,
        id: generateFieldId(),
        isNew: false,
        isModified: false,
        previousColumnName: col.columnName,
    };
}

function fieldToColumn(field: SchemaField) {
    const hasValidReferences = field.references
        && typeof field.references === 'object'
        && field.references.table;

    return {
        columnName: field.columnName,
        dataType: field.dataType,
        constraints: field.constraints || [],
        description: field.description || '',
        ...(field.default ? { default: field.default } : {}),
        ...(hasValidReferences ? { references: field.references } : {}),
        validation: field.validation
            ? (Array.isArray(field.validation) ? field.validation.flat().join(',') : String(field.validation))
            : '',
    };
}

const SchemaPropertiesTab: React.FC<SchemaPropertiesTabProps> = ({
 schema, onSaved
}) => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();
    const {
 addNewNotifcation
} = useContext(Context);

    const [fields, setFields] = useState<SchemaField[]>([]);
    const [entityName, setEntityName] = useState(schema.tableName);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const loadSchema = async () => {
            if (!appId || !oragID) return;
            setIsLoading(true);
            try {
                const res = await bexSchemaService.retrieveSchemas(schema.tableName, appId, oragID, schema.connectionId);
                const rawSchemas = res.data?.schemas || res.data || [];
                let columns: SchemaColumnDataModel[] = [];

                if (Array.isArray(rawSchemas)) {
                    rawSchemas.forEach((item: any) => {
                        if (Array.isArray(item.columns)) {
                            columns = item.columns;
                        } else if (item.columnName) {
                            columns.push(item);
                        }
                    });
                }

                setFields(columns.filter((c) => c.dataType !== 'equation').map(columnToField));
            } catch (error) {
                console.error('Error loading schema:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadSchema();
    }, [schema.tableName, appId, oragID]);

    const handleSave = useCallback(async () => {
        if (!oragID || !appId) return;
        if (fields.length === 0) {
            addNewNotifcation('Please add at least one field', 'danger');
            return;
        }

        setIsSaving(true);
        try {
            const newFields = fields.filter((f) => f.isNew);
            const modifiedFields = fields.filter((f) => f.isModified && !f.isNew);

            if (newFields.length > 0 || modifiedFields.length > 0) {
                const modifyPayload = {
                    schemas: [{
                        tableName: schema.tableName,
                        modifications: {
                            columns: {
                                ...(newFields.length > 0 ? { add: newFields.map(fieldToColumn) } : {}),
                                ...(modifiedFields.length > 0 ? {
                                    modify: modifiedFields.map((f) => ({
                                        ...fieldToColumn(f),
                                        originalColumnName: f.previousColumnName || f.columnName,
                                    })),
                                } : {}),
                            },
                        },
                    }],
                };
                await bexSchemaService.modifySchemas(modifyPayload, appId, oragID, schema.connectionId);
            }

            addNewNotifcation('Schema updated successfully', 'success');
            onSaved();
        } catch (error: any) {
            console.error('Save failed:', error);
            addNewNotifcation(error.message || 'Failed to save schema', 'danger');
        } finally {
            setIsSaving(false);
        }
    }, [oragID, appId, schema.tableName, fields, addNewNotifcation, onSaved]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {/* Settings section */}
            <div className="flex items-center gap-4 p-4 border rounded-md bg-muted/30">
                <div className="flex-1 space-y-1">
                    <Label className="text-xs text-muted-foreground">Display Name</Label>
                    <Input
                        value={entityName}
                        onChange={(e) => setEntityName(e.target.value)}
                        placeholder="e.g., User Profiles"
                        className="h-8"
                    />
                </div>
                <div className="flex-1 space-y-1">
                    <Label className="text-xs text-muted-foreground">Table Name</Label>
                    <Input value={schema.tableName} disabled className="h-8 bg-muted" />
                </div>
                <div className="self-end">
                    <Button size="sm" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? (
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Save className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>

            {/* Fields section */}
            <SchemaFieldsTab
                fields={fields}
                setFields={setFields}
                isNewSchema={false}
            />
        </div>
    );
};

export default SchemaPropertiesTab;
